"use client";

// ============================================================
// CLUBHUB – Was man mit einer Bestätigung tut
// ============================================================
// Drucken (nur das Blatt, A4), als PDF laden (`/api/spenden/<id>`,
// das abgelegte Doppel), den Versand vermerken und widerrufen (mit
// Begruendung — die Spenden werden wieder frei und koennen neu
// bestätigt werden). Solange clubhub keine Mail mit Anhang verschickt
// (`mailVersand`), gibt es nur „per Post versendet“ zu vermerken.
// ============================================================

import { useState } from "react";
import { Ban, Download, Mail, Printer, Send } from "lucide-react";
import {
  Button,
  Dialog,
  DialogCancel,
  DialogChoice,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogForm,
  DialogHeader,
  DialogNotice,
  DialogSubmit,
  DialogTitle,
  DropdownItem,
  DropdownMenu,
  MehrKnopf,
  Textarea,
} from "@/components/ui";
import type { CommunicationChannel } from "@/generated/prisma/enums";
import { bestaetigungenVersenden, bestaetigungWiderrufen } from "@/app/(app)/spenden/actions";

export function BelegAktionen({
  id,
  nummer,
  widerrufen,
  versendet,
  email,
  vorschlag,
  darfSchreiben,
  mailVersand = true,
}: {
  id: string;
  nummer: string;
  widerrufen: boolean;
  versendet: boolean;
  email: string | null;
  vorschlag: CommunicationChannel;
  darfSchreiben: boolean;
  /** Verschickt clubhub die Mail selbst? Sonst nur den Postversand vermerken. */
  mailVersand?: boolean;
}) {
  const [versenden, setVersenden] = useState(false);
  const [widerruf, setWiderruf] = useState(false);
  const mitMail = Boolean(email) && mailVersand;
  const [weg, setWeg] = useState<CommunicationChannel>(mitMail ? vorschlag : "POST");
  const [fehler, setFehler] = useState("");

  function pdf() {
    // Eine Datei, keine Seite: ueber einen Verweis laden statt zu navigieren.
    const verweis = document.createElement("a");
    verweis.href = `/api/spenden/${encodeURIComponent(id)}?download=1`;
    verweis.download = "";
    verweis.click();
  }

  return (
    <>
      <Button variant="outline" onClick={() => window.print()}>
        <Printer className="h-4 w-4" aria-hidden />
        Drucken
      </Button>
      {darfSchreiben && !widerrufen && (
        <Button variant={versendet ? "outline" : "default"} onClick={() => setVersenden(true)}>
          <Send className="h-4 w-4" aria-hidden />
          {mailVersand ? (versendet ? "Erneut senden" : "Versenden") : versendet ? "Versand erneut vermerken" : "Versand vermerken"}
        </Button>
      )}
      <DropdownMenu trigger={<MehrKnopf />} align="right">
        <DropdownItem onClick={pdf}>
          <Download className="h-4 w-4" aria-hidden />
          Als PDF laden
        </DropdownItem>
        {darfSchreiben && !widerrufen && (
          <DropdownItem variant="danger" onClick={() => setWiderruf(true)}>
            <Ban className="h-4 w-4" aria-hidden />
            Widerrufen
          </DropdownItem>
        )}
      </DropdownMenu>

      {versenden && (
        <Dialog open onClose={() => setVersenden(false)}>
          <DialogForm
            action={() => bestaetigungenVersenden([id], weg)}
            success={weg === "EMAIL" ? `${nummer} per Mail versendet` : mailVersand ? `${nummer} für den Briefversand vorgemerkt` : `Versand von ${nummer} per Post vermerkt`}
          >
            <DialogHeader>
              <DialogTitle>{mailVersand ? `${nummer} versenden` : `Versand von ${nummer} vermerken`}</DialogTitle>
              <DialogDescription>
                {mailVersand
                  ? "Die Bestätigung geht als PDF hinaus — per Mail oder als Brief."
                  : "clubhub verschickt noch keine Mail mit Anhang. Lade das PDF, schick es selbst und vermerke hier, dass es per Post hinausging."}
              </DialogDescription>
            </DialogHeader>
            <DialogContent>
              <DialogChoice
                label="Weg"
                value={weg}
                onChange={setWeg}
                options={[
                  ...(mitMail ? [{ value: "EMAIL" as const, label: "Per E-Mail", hint: `An ${email}, mit der Bestätigung als PDF im Anhang` }] : []),
                  { value: "POST" as const, label: "Per Post", hint: "Kommt in den Stapel zum Drucken — mit Anschriftfeld für den Fensterumschlag" },
                ]}
              />
              {!email && mailVersand && (
                <DialogNotice icon={<Mail className="text-text-tertiary h-4 w-4" aria-hidden />}>
                  Für diese Person ist keine Mailadresse hinterlegt.
                </DialogNotice>
              )}
            </DialogContent>
            <DialogFooter>
              <DialogCancel />
              <DialogSubmit>{weg === "EMAIL" ? "Jetzt senden" : mailVersand ? "Für den Druck vormerken" : "Als per Post versendet vermerken"}</DialogSubmit>
            </DialogFooter>
          </DialogForm>
        </Dialog>
      )}

      {widerruf && (
        <Dialog open onClose={() => setWiderruf(false)}>
          <DialogForm
            action={(fd) => bestaetigungWiderrufen(id, String(fd.get("grund") ?? ""))}
            success={{ title: `${nummer} widerrufen`, description: "Die Spenden sind wieder frei und lassen sich neu bestätigen." }}
            onError={(message) => setFehler(message)}
          >
            <DialogHeader>
              <DialogTitle>Bestätigung widerrufen?</DialogTitle>
              <DialogDescription>
                Die Nummer {nummer} bleibt belegt und die Bestätigung im Archiv — als widerrufen. Die enthaltenen Spenden lassen sich danach neu bestätigen.
              </DialogDescription>
            </DialogHeader>
            <DialogContent>
              <DialogNotice tone="warning">
                Ist die Bestätigung schon beim Spender, fordere sie zurück: Der Verein haftet für eine unrichtige Bestätigung (§ 10b Abs. 4 EStG).
              </DialogNotice>
              {/* BACKEND: Mitteilung an den Zuwendenden mit der Bitte, die alte Bestätigung zu vernichten bzw. nicht zu verwenden. */}
              <Textarea name="grund" label="Grund" rows={3} required placeholder="Betrag falsch übernommen (500 € statt 50 €)" error={fehler || undefined} />
            </DialogContent>
            <DialogFooter>
              <DialogCancel />
              <DialogSubmit variant="danger">Widerrufen</DialogSubmit>
            </DialogFooter>
          </DialogForm>
        </Dialog>
      )}
    </>
  );
}
